import type {
  CSSProperties,
  ComponentType,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
} from 'react';
import { useState } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router';
import { useMeasure } from 'react-use';
import styled, { css } from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { BottomPortal } from '@sorare/core/src/atoms/layout/BottomPortal';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL } from '@sorare/core/src/atoms/typography';
import { InstantCardSearch } from '@sorare/core/src/components/search/InstantSearch';
import type { AnalyticTag } from '@sorare/core/src/components/search/InstantSearch/types';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import {
  LeftFilters,
  SearchLayoutContainer,
  SearchLayoutMain,
  VerticalScroll,
} from '@sorare/core/src/components/search/SearchLayout/ui';
import { ANY_SPORT_MY_CARDS_VAULT_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import type {
  AlgoliaCardIndexesNames,
  GallerySortNames,
} from '@sorare/core/src/contexts/config';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSearchCardsContext } from '@sorare/core/src/contexts/searchCards';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useTransitionApi } from '@sorare/core/src/hooks/ui/useTransitionApi';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useToggle } from '@sorare/core/src/hooks/useToggle';
import type { FiltersGroup } from '@sorare/core/src/lib/filters';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { MyBidsBannerWrapper } from '@sorare/marketplace/src/components/auction/MyBidsBanner';
import { RarityVaultBannerWithFetch } from '@sorare/marketplace/src/components/sealing/RarityVaultBanner/index.withFetch';
import { CurrentMarketplaceUserProvider } from '@sorare/marketplace/src/contexts/currentMarketplaceUser/Provider';
import { useUserCardsSearchClient } from '@sorare/marketplace/src/hooks/search/useUserCardsSearchClient';
import { useDefaultFilters } from '@sorare/marketplace/src/hooks/useDefaultFilters';
import type { MyCardsSelectionMode } from '@sorare/marketplace/src/lib/my-cards';
import { ActiveFilters } from '@sorare/marketplace/src/search/ActiveFilters';
import { FiltersManager } from '@sorare/marketplace/src/search/FiltersManager';
import { FiltersManagerDialog } from '@sorare/marketplace/src/search/FiltersManagerDialog';
import { SearchBox } from '@sorare/marketplace/src/search/SearchBox';
import { ToggleFiltersManager } from '@sorare/marketplace/src/search/ToggleFiltersManager';
import { messages } from '@sorare/marketplace/src/searchCards/AdvancedCardSearch/SearchLayout';
import type { CardResultsProps } from '@sorare/marketplace/src/searchCards/AdvancedCardSearch/types';
import { CardResultsFromGraphQL } from '@sorare/marketplace/src/searchCards/CardResultsFromGraphQL';
import { SortCards } from '@sorare/marketplace/src/searchCards/SortCards';
import { TotalResults } from '@sorare/marketplace/src/searchCards/TotalResults';

import { RevealNavigationSwitch } from 'components/myCards/RevealNavigationSwitch';

import { CardOrCollectionRaritySwitcher } from '../CardOrCollectionRaritySwitcher';
import { CardsBoostSummary } from '../CardsBoostSummary';
import { CardsDropSummary } from '../CardsDropSummary';
import { CardsSealingSummary } from '../CardsSealingSummary';
import { CollectionNavigationSwitch } from '../CollectionNavigationSwitch';
import { DropOnboarding } from '../DropOnboarding';
import { RevealableCardRaritySwitcher } from '../RevealableCardRaritySwitcher';
import { SelectionModeRadios } from '../SelectionModeRadios';

const SearchCtn = styled(Vertical).attrs({ gap: 2 })`
  padding: 0 0 var(--double-unit);
`;
const SearchRow = styled(Horizontal).attrs({ gap: 1.5 })`
  justify-content: space-between;
`;
const SortAndSegment = styled(Vertical).attrs({ gap: 1.5 })`
  @media ${laptopAndAbove} {
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: flex-start;
  }
`;
const SortCtn = styled(Horizontal)`
  width: 100%;
  padding-bottom: var(--intermediate-unit);
  border-bottom: 1px solid rgba(var(--c-rgb-white), 0.11);
  @media ${laptopAndAbove} {
    width: auto;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const StyledSearchLayoutMain = styled(SearchLayoutMain)<{
  $revealCards?: boolean;
}>`
  min-height: calc(
    100vh - var(--current-stack-height) - var(--summary-button-height)
  );
  ${({ $revealCards = false }) =>
    $revealCards &&
    css`
      margin: 0 calc(var(--unit) * -2);
      width: calc(100 * var(--1vw));
      @media ${tabletAndAbove} {
        margin: 0 calc(var(--unit) * -3);
        width: auto;
      }
    `}
`;

const SummaryWrapper = styled(Vertical)`
  padding: var(--double-unit) var(--unit);
  &.portalDisabled {
    position: sticky;
    bottom: 0;
    :root:has(${MyBidsBannerWrapper}) & {
      bottom: 80px;
    }
  }
`;

const RightContentWrapper = styled(Vertical)`
  width: 100%;
  position: relative;
  isolation: isolate;
`;

const ScrollPlaceHolder = styled.div`
  height: 64px;
`;

const PositionOver3DScene = styled(Vertical)`
  position: absolute;
  top: var(--double-unit);
  left: var(--double-unit);
  right: var(--double-unit);
  z-index: 1;
  @media ${laptopAndAbove} {
    top: var(--triple-unit);
  }
`;

const blockchainSorts: GallerySortNames = [
  'Cards New',
  'Cards Highest Average Score',
  'Cards Highest Price',
  'Cards Lowest Price',
  'Cards Player Name',
  'Sealing Value',
];

const commonSorts: GallerySortNames = [
  'Cards New',
  'Cards Highest Average Score',
  'Cards Player Name',
];

type MyCardsSearchContent = {
  sport: Sport;
  cardFilters: FiltersGroup[];
  rarity?: Rarity;
  rarityPath?: (rarity: Rarity) => string;
  sealed?: boolean;
  selectionMode: MyCardsSelectionMode;
  setSelectionMode: Dispatch<SetStateAction<MyCardsSelectionMode>>;
  PrependedItemComponent?: ComponentType<{ total?: number }>;
  sorts: AlgoliaCardIndexesNames | GallerySortNames;
  GridComponent?: ComponentType<CardResultsProps>;
  revealCards?: boolean;
};
const MyCardsSearchContent = ({
  cardFilters,
  rarity,
  sealed,
  selectionMode,
  setSelectionMode,
  sport,
  rarityPath,
  sorts,
  PrependedItemComponent,
  GridComponent = CardResultsFromGraphQL,
  revealCards,
}: MyCardsSearchContent) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { currentUser } = useCurrentUserContext();
  const { generateSportContextPath } = useSportContext();
  const { up: isLaptop } = useScreenSize('laptop');
  const [searchParams, setSearchParams] = useSearchParams();
  const { lifecycle, update } = useLifecycle();
  const seriesNav = useSeriesNav();
  const lastVisitedRarity =
    lifecycle?.[LIFECYCLE.lastVisitedMyCardsRarity]?.[sport];

  const vaultTypePath = generateSportContextPath(
    ANY_SPORT_MY_CARDS_VAULT_TYPE,
    {
      params: {
        type:
          // we don't have common Vault so switch to Limited upon visiting Vault
          rarity === Rarity.common ? Rarity.limited : rarity || Rarity.limited,
      },
    }
  );

  const { updateDOM } = useTransitionApi();

  const [ref, { height: summaryHeight }] = useMeasure<HTMLDivElement>();
  const [filtersOpen, toggleFilters] = useToggle(false);
  const [selectedCardsByRarity, setSelectedCardsByRarity] = useState<{
    rarity: Rarity | undefined;
    cards: { slug: string; xpNeeded?: number; sealableFor?: number }[];
  }>({ rarity, cards: [] });
  const selectedCards = rarity ? selectedCardsByRarity.cards : [];
  const selectedCardSlugs = selectedCards.map(({ slug }) => slug);
  const totalXpNeeded = selectedCards.reduce(
    (acc, { xpNeeded = 0 }) => acc + xpNeeded,
    0
  );
  const { refresh } = useInstantSearch();

  const setSelectedCards = (
    cards: {
      slug: string;
      xpNeeded?: number;
      sealableFor?: number;
    }[]
  ) => {
    if (rarity) {
      setSelectedCardsByRarity({ rarity, cards });
    }
  };

  const onToggleCard = (
    anyCardSlug: string,
    options?: { xpNeeded?: number; sealableFor?: number }
  ) => {
    updateDOM(() => {
      setSelectedCardsByRarity(prev => {
        const selected = !!prev.cards.find(({ slug }) => slug === anyCardSlug);
        return selected
          ? {
              ...prev,
              cards: prev.cards.filter(({ slug }) => slug !== anyCardSlug),
            }
          : {
              ...prev,
              cards: [
                ...prev.cards,
                {
                  slug: anyCardSlug,
                  xpNeeded: options?.xpNeeded,
                  sealableFor: options?.sealableFor,
                },
              ],
            };
      });
    });
  };

  const resetSelection = (options = { resetSelectionMode: true }) => {
    if (options?.resetSelectionMode) {
      setSelectionMode(undefined);
      searchParams.delete('selectionMode');
      setSearchParams(searchParams, { replace: true });
    }
    setSelectedCards([]);
  };

  if (rarity && rarity !== selectedCardsByRarity.rarity) {
    resetSelection({
      resetSelectionMode:
        selectionMode === 'drop' || selectionMode === 'sealing',
    });
  }

  const canSeal = rarity !== Rarity.common && !sealed;
  const isOnDroppableSport = sport === Sport.NBA || sport === Sport.FOOTBALL;

  const canDrop = isOnDroppableSport && rarity === Rarity.common && !sealed;
  const canBoost = !sealed;
  const showActionsButton =
    !selectionMode && !isLaptop && (canDrop || canBoost) && !revealCards;

  const showBottomWrapper = showActionsButton || selectionMode;
  const searchPlaceholder = rarity
    ? messages.rarityCardsPlaceholder
    : messages.defaultPlaceholder;

  const displayDropOnboarding =
    canDrop && !lifecycle?.[LIFECYCLE.sawDropOnboarding];

  const updateLastVisitedRarity = (clickedRarity: Rarity) => {
    update(LIFECYCLE.lastVisitedMyCardsRarity, {
      ...(lifecycle?.[LIFECYCLE.lastVisitedMyCardsRarity] || {}),
      [sport]: clickedRarity,
    });
  };

  const additionalPoints =
    sport === Sport.BASEBALL
      ? selectedCardSlugs?.length
      : selectedCards.reduce(
          (acc, { sealableFor = 0 }) => acc + sealableFor,
          0
        );

  return (
    <>
      <SearchLayoutContainer>
        {!!isLaptop && (
          <VerticalScroll>
            <LeftFilters visible>
              <Vertical gap={2}>
                {!!rarity && !!rarityPath && !revealCards && (
                  <CardOrCollectionRaritySwitcher
                    type={rarity}
                    rarityPath={rarityPath}
                    onClick={updateLastVisitedRarity}
                    user={currentUser}
                    sealed={sealed}
                  />
                )}
                {!!revealCards && (
                  <RevealableCardRaritySwitcher
                    selectedRarity={lastVisitedRarity as Rarity}
                    onChange={updateLastVisitedRarity}
                  />
                )}
                <Vertical gap={0}>
                  <SearchCtn>
                    <SearchBox placeholder={formatMessage(searchPlaceholder)} />
                    <SearchRow>
                      <TotalResults />
                    </SearchRow>
                  </SearchCtn>
                  <FiltersManager filters={cardFilters} skipClearAllButton />
                </Vertical>
              </Vertical>
            </LeftFilters>
          </VerticalScroll>
        )}
        <RightContentWrapper>
          <StyledSearchLayoutMain
            $revealCards={revealCards}
            style={
              {
                '--summary-button-height': `${summaryHeight}px`,
                ...(revealCards ? { padding: 0 } : {}),
              } as CSSProperties
            }
          >
            <ConditionalWrapper
              wrap={!!revealCards}
              Wrapper={PositionOver3DScene}
            >
              <SortAndSegment>
                {!!isLaptop && (
                  <SortCtn>
                    <SortCards indexes={sorts} />{' '}
                  </SortCtn>
                )}
                <SBHorizontal>
                  {!isLaptop && (
                    <>
                      {!!currentUser && !!seriesNav && (
                        <CollectionNavigationSwitch
                          user={currentUser}
                          rarity={rarity}
                        />
                      )}
                      <div>
                        <ToggleFiltersManager
                          toggleFilters={toggleFilters}
                          color="quaternary"
                          size="small"
                        />
                      </div>
                    </>
                  )}
                  {selectionMode === 'drop' && (
                    <HeadlineL>
                      <FormattedMessage
                        id="MyCardsSearch.scrapTitle"
                        defaultMessage="Select Cards to scrap"
                      />
                    </HeadlineL>
                  )}
                  {selectionMode === 'boost' && (
                    <HeadlineL>
                      <FormattedMessage
                        id="MyCardsSearch.levelUpTitle"
                        defaultMessage="Select cards to level up"
                      />
                    </HeadlineL>
                  )}
                  {selectionMode === 'sealing' && (
                    <HeadlineL>
                      <FormattedMessage
                        id="MyCardsSearch.sealingTitle"
                        defaultMessage="Select cards to seal"
                      />
                    </HeadlineL>
                  )}
                  {!selectionMode && !sealed && (
                    <RevealNavigationSwitch
                      rarity={rarity}
                      user={currentUser!}
                    />
                  )}
                </SBHorizontal>
              </SortAndSegment>
              <ActiveFilters />
            </ConditionalWrapper>
            {rarity !== Rarity.common && !revealCards && (
              <RarityVaultBannerWithFetch
                rarity={rarity || Rarity.limited}
                additionalPoints={
                  (selectionMode === 'sealing' && additionalPoints) || 0
                }
                hideEmptyVault={sealed}
                showSealingOnboardingWhenApplicable={
                  !(sealed || selectionMode === 'sealing')
                }
              />
            )}
            {!!showActionsButton && (
              <SelectionModeRadios
                onClick={setSelectionMode}
                canDrop={canDrop}
                canSeal={canSeal}
                canBoost={canBoost}
                displayDropOnboarding={displayDropOnboarding}
              />
            )}
            <GridComponent
              hideOwner
              galleryOwnerSlug={currentUser?.slug}
              showDesktopFilter
              sport={sport}
              editableLists
              sealed={sealed}
              selectionMode={selectionMode}
              setSelectionMode={setSelectionMode}
              totalXpNeeded={totalXpNeeded}
              canDrop={canDrop}
              canLevelUp={canBoost}
              hideScoutShortcuts
              selectedCardSlugs={selectedCardSlugs}
              onToggleCard={onToggleCard}
              showActionButtonOnFirstCard={displayDropOnboarding}
              PrependedItemComponent={PrependedItemComponent}
            />
            {!!showBottomWrapper && !!selectionMode && <ScrollPlaceHolder />}
          </StyledSearchLayoutMain>
          {!!showBottomWrapper && (
            <BottomPortal.Above>
              <SummaryWrapper ref={ref} center={!showActionsButton}>
                {selectionMode === 'drop' && (
                  <CardsDropSummary
                    rarity={rarity || Rarity.common}
                    selectedCardsSlugs={selectedCardSlugs}
                    onCancel={() => {
                      updateDOM(() => {
                        resetSelection();
                      });
                    }}
                    onDropped={() => {
                      refresh();
                      updateDOM(() => {
                        resetSelection();
                      });
                    }}
                  />
                )}
                {selectionMode === 'boost' && (
                  <CardsBoostSummary
                    rarity={rarity || Rarity.common}
                    selectedCardsSlugs={selectedCardSlugs}
                    onCancel={() => {
                      updateDOM(() => {
                        resetSelection();
                      });
                    }}
                    onBoosted={() => {
                      updateDOM(() => {
                        resetSelection({ resetSelectionMode: false });
                      });
                    }}
                  />
                )}
                {selectionMode === 'sealing' && (
                  <CardsSealingSummary
                    rarity={rarity || Rarity.limited}
                    selectedCardsSlugs={selectedCardSlugs}
                    onCancel={() => {
                      updateDOM(() => {
                        resetSelection();
                      });
                    }}
                    onConfirm={() => {
                      updateDOM(() => {
                        resetSelection({ resetSelectionMode: true });
                        navigate(vaultTypePath);
                      });
                    }}
                  />
                )}
              </SummaryWrapper>
            </BottomPortal.Above>
          )}
        </RightContentWrapper>
      </SearchLayoutContainer>
      {!isLaptop && (
        <FiltersManagerDialog
          open={filtersOpen}
          onClose={toggleFilters}
          filtersGroups={cardFilters}
          sorts={sorts}
        >
          <Vertical>
            <SearchBox placeholder={formatMessage(searchPlaceholder)} />
            <SearchRow>
              <TotalResults />
            </SearchRow>
          </Vertical>
        </FiltersManagerDialog>
      )}
      {!!displayDropOnboarding && (
        <DropOnboarding
          onClose={() => {
            update(LIFECYCLE.sawDropOnboarding, true);
          }}
        />
      )}
      {!isLaptop && !!revealCards && sport !== Sport.BASEBALL && (
        <BottomPortal>
          <RevealableCardRaritySwitcher
            selectedRarity={lastVisitedRarity as Rarity}
            onChange={updateLastVisitedRarity}
          />
        </BottomPortal>
      )}
    </>
  );
};

type Props = {
  sport: Sport;
  analyticsTags: AnalyticTag[];
  cardFilters: FiltersGroup[];
  rarity?: Rarity;
  rarityPath?: (rarity: Rarity) => string;
  sealed?: boolean;
  PrependedItemComponent?: ComponentType<{ total?: number }>;
  GridComponent?: ComponentType<CardResultsProps>;
  revealCards?: boolean;
};
const InstantCardSearchWithDuplicatedKey = ({
  sport,
  analyticsTags,
  cardFilters,
  rarity,
  sealed,
  children,
  rarityPath,
  PrependedItemComponent,
  GridComponent,
  revealCards,
}: PropsWithChildren<Props>) => {
  const {
    so5: { cardMaxGrade },
  } = useConfigContext();
  const { currentUser } = useCurrentUserContext();
  const [searchParams] = useSearchParams();
  const { duplicatedKey } = useSearchCardsContext();

  const selectionModeFromSearchParams = searchParams.get('selectionMode');
  const defaultMode =
    selectionModeFromSearchParams &&
    ['drop', 'boost', 'sealing'].includes(selectionModeFromSearchParams)
      ? (selectionModeFromSearchParams as MyCardsSelectionMode)
      : undefined;
  const [selectionMode, setSelectionMode] =
    useState<MyCardsSelectionMode>(defaultMode);

  if (!selectionMode && defaultMode) {
    setSelectionMode(defaultMode);
  }

  const getDefaultRarity = () => {
    if (!rarity) {
      return undefined;
    }
    const customSeriesFilterEnabled =
      searchParams.get(SEARCH_PARAMS.NON_PLAYABLE_CARDS) === 'true';
    if (customSeriesFilterEnabled) {
      return Rarity.custom_series;
    }
    return rarity;
  };

  const defaultFilters = useDefaultFilters({
    userId: idFromObject(currentUser?.id),
    extraFilters:
      selectionMode === 'boost' ? `grade<${cardMaxGrade}` : undefined,
  });

  const searchClient = useUserCardsSearchClient(currentUser?.slug || '', {
    rarity: revealCards ? undefined : getDefaultRarity(),
    sport,
    sealed,
    duplicatedKey: duplicatedKey || undefined,
  });

  const sorts = rarity === Rarity.common ? commonSorts : blockchainSorts;
  return (
    <InstantCardSearch
      indexes={[sorts[0]]}
      availableSorts={sorts}
      analyticsTags={analyticsTags}
      sport={sport}
      defaultFilters={defaultFilters}
      urlState
      searchClient={searchClient}
    >
      <MyCardsSearchContent
        cardFilters={cardFilters}
        rarity={rarity}
        selectionMode={selectionMode}
        setSelectionMode={setSelectionMode}
        sport={sport}
        rarityPath={rarityPath}
        sealed={sealed}
        sorts={sorts}
        PrependedItemComponent={PrependedItemComponent}
        GridComponent={GridComponent}
        revealCards={revealCards}
      />
      {children}
    </InstantCardSearch>
  );
};

export const MyCardsSearch = ({
  sport,
  sealed,
  ...props
}: PropsWithChildren<Props>) => {
  const { currentUser } = useCurrentUserContext();

  return (
    <CurrentMarketplaceUserProvider sport={sport}>
      <SearchCardsContextProvider
        galleryOwnerSlug={currentUser?.slug}
        sealed={sealed}
        editableLists
      >
        <InstantCardSearchWithDuplicatedKey
          sport={sport}
          sealed={sealed}
          {...props}
        />
      </SearchCardsContextProvider>
    </CurrentMarketplaceUserProvider>
  );
};
